import type { CSSProperties } from 'react'
import { blockTheme, hsl, shadowToken } from '../block-theme/blockTheme'
import { cn } from '../lib/utils'
import { uiText } from '../i18n'
import { PRESSABLE_CLASS } from './pressable'

const TEXT = {
  ru: { gotIt: 'ПОНЯТНО', check: 'ПРОВЕРИТЬ', next: 'ДАЛЕЕ' },
  en: { gotIt: 'GOT IT', check: 'CHECK', next: 'NEXT' },
}

export type LessonCtaButtonState = 'idle' | 'correct' | 'partial' | 'incorrect'

interface LessonCtaButtonProps {
  state?: LessonCtaButtonState
  label?: string
  disabled?: boolean
  onClick?: () => void
}

const q = blockTheme.quiz

interface Look {
  bg: string
  ink: string
  gradient: string
  glow: string
  fg: string
}

/**
 * Вид пилюли по состоянию — порт плеерного `CtaButton` (academy-frontend
 * `.../blocks/CtaButton/CtaButton.module.css`): основная фиолетовая, зелёная и
 * красная вердикта (градиент, тёмная кромка 6px, светлый кант — `PRESSABLE_CLASS`)
 * и приглушённая погашенная. `partial` в плеере нет — оставлен прежний янтарный.
 */
const lookOf = (state: LessonCtaButtonState, disabled: boolean): Look => {
  if (disabled)
    return {
      bg: q.muted,
      ink: q.mutedInk,
      gradient: q.gloss,
      glow: q.muted,
      fg: q.mutedText,
    }
  switch (state) {
    case 'correct':
      return {
        bg: q.success,
        ink: q.successInk,
        gradient: q.successGradient,
        glow: q.success,
        fg: q.primaryFg,
      }
    case 'incorrect':
      return {
        bg: q.destructive,
        ink: q.destructiveInk,
        gradient: q.destructiveGradient,
        glow: q.destructive,
        fg: q.primaryFg,
      }
    case 'partial':
      return {
        bg: hsl(blockTheme.partial),
        ink: hsl(shadowToken(blockTheme.partial)),
        gradient: q.gloss,
        glow: hsl(blockTheme.partial),
        fg: q.primaryFg,
      }
    case 'idle':
      return {
        bg: q.primary,
        ink: q.primaryInk,
        gradient: q.gloss,
        glow: q.primary,
        fg: q.primaryFg,
      }
  }
}

const raised = (look: Look): CSSProperties =>
  ({
    backgroundColor: look.bg,
    backgroundImage: look.gradient,
    color: look.fg,
    '--cta-ink': look.ink,
    '--cta-glow': look.glow,
  }) as CSSProperties

/** LessonCtaButton — нижняя объёмная пилюля урока («ПОДТВЕРДИТЬ» / «ДАЛЕЕ» / «ПОНЯТНО»). */
export const LessonCtaButton = ({
  state = 'idle',
  label,
  disabled = false,
  onClick,
}: LessonCtaButtonProps) => {
  const resolvedLabel =
    label ??
    (state === 'incorrect'
      ? uiText(TEXT).gotIt
      : state === 'idle'
        ? uiText(TEXT).check
        : uiText(TEXT).next)

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        PRESSABLE_CLASS,
        'flex h-12 max-w-[500px] flex-1 items-center justify-center whitespace-nowrap text-[18px] font-bold uppercase leading-none tracking-[0.04em] disabled:cursor-not-allowed',
      )}
      style={raised(lookOf(state, disabled))}
    >
      <span>{resolvedLabel}</span>
    </button>
  )
}
